import React, { useState } from "react";

const experience = [
	{
		role: "Senior Learning Systems Engineer",
		company: "Western Governors University",
		duration: "September 2024 - Present",
		category: "Tech & Education",
		categoryColor: "bg-primary text-white",
		borderColor: "border-primary",
		points: [
			"Designs and develops high-quality, engaging online courses and assessments aligned with WGU standards, competencies, and real-world skills.",
			"Collaborates with SMEs and cross-functional partners to create objective-driven content, multimedia materials, and interactive learning experiences.",
			"Builds and maintains courses in Open edX using HTML, CSS, JavaScript, and AI-powered tools to strengthen student outcomes and instructional efficiency.",
			"Partners with external vendors to implement novel, data-driven enhancements that improve KPI performance and operational efficiency.",
			"Conducts quality assurance reviews to ensure accuracy, consistency, and alignment between learning objectives, resources, and assessments.",
			"Develops and deploys scalable technical solutions—including an automated voucher system (AWS, Node.js/Express.js, MongoDB)—that eliminate support gaps and drive measurable impact.",
		],
	},
	{
		role: "Web Developer and Designer (Contract)",
		company: "Monilis Design",
		duration: "September 2022 - September 2024",
		category: "Engineering",
		categoryColor: "bg-secondary text-white",
		borderColor: "border-secondary",
		points: [
			"Engaged clients through individualized consultations to understand requirements, preferences, and business objectives, delivering customized web solutions aligned to their needs.",
			"Acted as the primary point of contact and provided ongoing support, troubleshooting, and post-deployment updates to maintain long-term project reliability.",
			"Designed and developed custom websites using React.js, Firebase, and WordPress—ensuring high performance, scalability, and security across front-end and CMS implementations.",
			"Managed the full project lifecycle from discovery and planning to execution and delivery, maintaining clear communication and incorporating client feedback to ensure project success.",
		],
	},
	{
		role: "Website Admin and Content Manager",
		company: "Gyeonggi Suwon International School",
		duration: "December 2019 - June 2023",
		category: "Engineering",
		categoryColor: "bg-secondary text-white",
		borderColor: "border-secondary",
		points: [
			"Created and customized CSS within Finalsite to ensure cohesive site design, improved child-page navigation, and optimized accessibility across devices.",
			"Managed and updated website content and digital assets while implementing SEO improvements to increase visibility and search performance.",
			"Collaborated with faculty, staff, and administration to gather feedback, coordinate content accuracy, and provide training and support on CMS usage.",
			"Reviewed and tested web pages for functionality, accuracy, and compliance, resolving issues promptly and using analytics tools to guide strategic enhancements.",
		],
	},
	{
		role: "Publications and ToK Teacher / Head of Department",
		company: "Gyeonggi Suwon International School",
		duration: "July 2019 - June 2023",
		category: "Leadership",
		categoryColor: "bg-accent text-white",
		borderColor: "border-accent",
		points: [
			"Led a team of 6 educators and redesigned the IB Theory of Knowledge curriculum for record-setting academic performance, resulting in the highest ToK score averages in the school's 20 year history.",
			"Revamped the Publications curriculum to streamline yearbook production, increasing efficiency and contributing to GSIS winning the NSPA 'Best of International Student Yearbook' award in 2021 and 2022.",
			"Taught web design and digital communication with HTML5, CSS3, and Bootstrap.",
		],
	},
	{
		role: "Design Consultant & Photographer",
		company: "Freelance / Knoxville, TN",
		duration: "August 2017 - June 2020",
		category: "Engineering",
		categoryColor: "bg-secondary text-white",
		borderColor: "border-secondary",
		points: [
			"Provided front-end web design consultation to help small businesses create visually appealing, user-friendly websites aligned with branding and marketing goals.",
			"Operated a photography studio serving small businesses, real estate agencies, and corporate clients, producing high-quality images edited in Adobe Photoshop and Lightroom.",
			"Managed projects from initial consultation through final delivery, ensuring clear communication, on-time execution, and strong marketing outcomes for clients.",
			"Oversaw end-to-end project workflows for both photography and design engagements, coordinating schedules, deliverables, and client expectations to ensure satisfaction and repeat business.",
		],
	},
	{
		role: "Middle School Science Teacher",
		company: "International School of Qingdao",
		duration: "August 2013 - June 2017",
		category: "Education",
		categoryColor: "bg-accent text-white",
		borderColor: "border-accent",
		points: [
			"Integrated NGSS standards into an international curriculum for grades 6-8.",
			"Facilitated STEM projects and mentored new teachers in planning and management.",
			"Improved student assessment scores by 12% through data-driven instruction.",
		],
	},
	{
		role: "Store Manager",
		company: "RadioShack",
		duration: "May 2010 - July 2013",
		category: "Leadership",
		categoryColor: "bg-primary text-white",
		borderColor: "border-primary",
		points: [
			"Led a team of 6-8 employees, developed product fluency training and sales goals.",
			"Improved inventory control and reduced shrinkage by 8% over two years.",
			"Implemented customer service strategies that exceeded monthly sales targets.",
		],
	},
	{
		role: "Rifleman",
		company: "United States Marine Corps",
		duration: "November 2007 - July 2009",
		category: "Military",
		categoryColor: "bg-gray-700 text-white",
		borderColor: "border-gray-500",
		points: [
			"Completed rigorous tactical training and contributed to mission execution in infantry units.",
			"Operated advanced weaponry and communication systems under strict protocols.",
			"Led a Fire-Team of 4 Marines, leading to recognition for reliability, discipline, and operational effectiveness.",
		],
	},
];

export default function ExperienceProjects() {
	const [expandedIndex, setExpandedIndex] = useState(null);

	const toggle = (idx) => setExpandedIndex(expandedIndex === idx ? null : idx);

	return (
		<div className="flex flex-col bg-slate-50 pt-0 pb-16">
			<div className="flex-1">
				<div className="max-w-4xl mx-auto px-4 md:px-0">
					<h1 className="text-4xl font-bold text-primary mb-10 mt-8 text-center">
						Experience &amp; Projects
					</h1>

					{/* Timeline */}
					<div className="relative">
						{/* Vertical line */}
						<div className="absolute left-3 top-2 bottom-2 w-0.5 bg-gray-300 hidden md:block" />

						<div className="space-y-6">
							{experience.map((item, idx) => (
								<div key={idx} className="relative md:pl-10">
									{/* Timeline dot */}
									<div className="absolute left-0 top-5 w-7 h-7 rounded-full bg-white border-2 border-gray-300 hidden md:flex items-center justify-center shadow-sm">
										<div className={`w-3 h-3 rounded-full ${item.categoryColor.split(" ")[0]}`} />
									</div>

									{/* Card */}
									<div className={`bg-white rounded-xl shadow-sm border-l-4 ${item.borderColor} overflow-hidden`}>
										{/* Card header — always visible, clickable */}
										<button
											onClick={() => toggle(idx)}
											className="w-full text-left px-5 py-4 flex items-start justify-between gap-4 group"
											aria-expanded={expandedIndex === idx}
										>
											<div className="flex-1 min-w-0">
												<div className="flex flex-wrap items-center gap-2 mb-1">
													<span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${item.categoryColor}`}>
														{item.category}
													</span>
													<span className="text-xs text-gray-400">{item.duration}</span>
												</div>
												<h2 className="text-base font-bold text-primary leading-snug">
													{item.role}
												</h2>
												<p className="text-sm text-gray-500">{item.company}</p>
											</div>
											<span
												className={`text-gray-400 text-lg mt-1 shrink-0 transition-transform duration-200 ${
													expandedIndex === idx ? "rotate-180" : ""
												}`}
											>
												▾
											</span>
										</button>

										{/* Expandable bullet points */}
										{expandedIndex === idx && (
											<div className="px-5 pb-5 border-t border-gray-100">
												<ul className="mt-3 space-y-2">
													{item.points.map((point, i) => (
														<li key={i} className="flex gap-2 text-sm text-gray-700 leading-relaxed">
															<span className="text-accent mt-1 shrink-0">•</span>
															<span>{point}</span>
														</li>
													))}
												</ul>
											</div>
										)}
									</div>
								</div>
							))}
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}
